import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { CreditCardOff, Trash } from 'tabler-icons-react';

import {
   NoItemsNotification,
   SectionContainer,
   StyledButton,
   StyledTable,
} from 'components/shared';
import { useCostTypeLabels } from 'constants/costTypeLabels';
import { usePalletsTypes } from 'constants/palletTypes';
import { useTableHeaders } from 'constants/tableHeaders';
import { useResponsiveLayout } from 'hooks/useResponsiveLayout';
import { Charge, OrderStatus } from 'interfaces';
import MobileCard from 'pages/ClientPanel/OrdersPage/OrderDetailsPage/MobileCard/MobileCard';
import { Banner, TableCell, TableRow } from 'storybook';
import { getDayLabel, isChargeCancelable } from 'utils/functions';
import { currencyFormatter } from 'utils/numberFormatter';

import styles from './AdditionalChargesSection.module.css';

type AdditionalChargesSectionProps = {
   additionalCharges: Charge[];
   chargesTotalAmount?: number;
   onAddAdditionalCharges?: () => void;
   supplierMode?: boolean;
   orderStatus?: OrderStatus;
   hasRestoredHiddenCharges?: boolean;
   isCancelChargeDisabled?: boolean;
   onCancelCharge?: (charge: Charge, chargeIndex: number) => void;
   onClickReportDamagedLostPallets?: () => void;
};

const AdditionalChargesSection = ({
   additionalCharges,
   chargesTotalAmount,
   onClickReportDamagedLostPallets,
   onAddAdditionalCharges,
   supplierMode,
   orderStatus,
   hasRestoredHiddenCharges,
   isCancelChargeDisabled,
   onCancelCharge,
}: AdditionalChargesSectionProps) => {
   const { isMobile } = useResponsiveLayout();
   const { t } = useTranslation();

   const {
      additionalCostsClientTableHeaders,
      additionalCostsSupplierTableHeaders,
      additionalCostsSupplierWithCancellationTableHeaders,
   } = useTableHeaders();

   const { PALLET_TYPE_LABELS } = usePalletsTypes();
   const { costTypeLabels } = useCostTypeLabels();

   const withCancellationColumn =
      !!supplierMode && !!onCancelCharge && orderStatus === 'DURING_BILLING';
   const columnsAmount = supplierMode ? (withCancellationColumn ? 7 : 6) : 4;

   const renderContent = () => {
      if (!supplierMode && isMobile) {
         if (!additionalCharges.length) {
            return (
               <NoItemsNotification
                  icon={<CreditCardOff color="#A6A6B2" size={48} />}
                  description={t('charges.noAdditionalCharges')}
               />
            );
         } else {
            return (
               <div>
                  {additionalCharges.map((additionalCost, index) => (
                     <MobileCard key={index} data={additionalCost} />
                  ))}
               </div>
            );
         }
      } else {
         return (
            <StyledTable
               columnHeaders={
                  supplierMode
                     ? withCancellationColumn
                        ? additionalCostsSupplierWithCancellationTableHeaders
                        : additionalCostsSupplierTableHeaders
                     : additionalCostsClientTableHeaders
               }
               className={classNames({
                  [styles.tableWithChargesSummary]: supplierMode,
                  [styles.tableWithCancellationColumn]: withCancellationColumn,
               })}
            >
               {isEmpty(additionalCharges) ? (
                  <tr>
                     <td colSpan={columnsAmount}>
                        <NoItemsNotification
                           icon={<CreditCardOff color="#A6A6B2" size={48} />}
                           description={t('charges.noAdditionalCharges')}
                           desktopSmallNoItemsNotification
                        />
                     </td>
                  </tr>
               ) : (
                  <>
                     {/* the row index is the charge position in the original, unfiltered
                     additionalCharges array - it is sent to the cancellation endpoint */}
                     {additionalCharges.map((additionalCost, index) => (
                        <TableRow
                           key={index}
                           className={classNames({
                              [styles.canceledChargeRow]: additionalCost.hidden,
                           })}
                        >
                           <TableCell>
                              {additionalCost.palletType
                                 ? PALLET_TYPE_LABELS[additionalCost.palletType]
                                 : '-'}
                           </TableCell>
                           <TableCell>
                              {additionalCost.otherChargeName ||
                                 costTypeLabels[additionalCost.chargeType]}
                           </TableCell>
                           <TableCell>
                              {additionalCost.retentionPeriodDays
                                 ? `${additionalCost.retentionPeriodDays} ${getDayLabel(
                                      additionalCost.retentionPeriodDays,
                                   )}`
                                 : '-'}
                           </TableCell>
                           <TableCell align="right">
                              {additionalCost.palletsAmount || '-'}
                           </TableCell>
                           {supplierMode && (
                              <>
                                 <TableCell align="right">
                                    {additionalCost.price
                                       ? currencyFormatter.format(additionalCost.price)
                                       : '-'}
                                 </TableCell>
                                 <TableCell align="right">
                                    {additionalCost.totalAmount
                                       ? currencyFormatter.format(additionalCost.totalAmount)
                                       : '-'}
                                 </TableCell>
                              </>
                           )}
                           {withCancellationColumn && (
                              <TableCell align="right" className={styles.cancelCell}>
                                 {isChargeCancelable(additionalCost) && (
                                    <StyledButton
                                       type="button"
                                       variant="text"
                                       icon={<Trash size={20} />}
                                       disabled={isCancelChargeDisabled}
                                       className={styles.cancelBtn}
                                       onClick={() => onCancelCharge?.(additionalCost, index)}
                                    />
                                 )}
                              </TableCell>
                           )}
                        </TableRow>
                     ))}
                     {supplierMode && additionalCharges.length !== 0 && chargesTotalAmount && (
                        <tr className={styles.chargesTotalAmountRow}>
                           <td></td>
                           <td></td>
                           <td></td>
                           <td></td>
                           <td colSpan={2} className={styles.chargesTotalAmountCell}>
                              <div className={styles.chargesTotalAmountContainer}>
                                 {t('charges.total')}:{' '}
                                 <span>{currencyFormatter.format(chargesTotalAmount)}</span>
                              </div>
                           </td>
                           {withCancellationColumn && <td></td>}
                        </tr>
                     )}
                  </>
               )}
            </StyledTable>
         );
      }
   };

   return (
      <SectionContainer title={t('charges.title')}>
         {supplierMode && hasRestoredHiddenCharges && (
            <Banner
               className={styles.restoredChargesBanner}
               fullWidth
               variant="warning"
               children={t('charges.restoredHiddenChargesWarning')}
            />
         )}
         {supplierMode && orderStatus === 'DELIVERED' && (
            <StyledButton
               variant="outlined-primary"
               text={t('charges.report')}
               onClick={onClickReportDamagedLostPallets}
               className={styles.additionalActionBtn}
            />
         )}
         {supplierMode && orderStatus === 'DURING_BILLING' && (
            <StyledButton
               variant="outlined-primary"
               text={t('charges.add')}
               onClick={onAddAdditionalCharges}
               className={styles.additionalActionBtn}
            />
         )}
         {renderContent()}
         {!supplierMode && additionalCharges.length !== 0 && (
            <Banner
               style={{ marginTop: 24 }}
               fullWidth
               variant="info"
               children={t('charges.clientHelp')}
            />
         )}
      </SectionContainer>
   );
};

export default AdditionalChargesSection;
